import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Barra, Bloque, Cifra, Pagina, Tabla } from '../componentes/Estructura';
import { Cargando, ConError, Vacio } from '../componentes/Estados';
import type { Tono } from '../componentes/Etiqueta';
import { haceCuanto, porcentaje } from '../componentes/formato';
import { useCargaReal } from '../dominio/carga';
import { obtenerPanel } from '../dominio/panelApi';
import { obtenerColaIncidentes } from '../dominio/incidentesApi';
import type { EstadoIncidenteReal, TipoIncidenteReal } from '../dominio/incidentesApi';
import { hayBackend } from '../config/entorno';

/** Debajo de esta tasa de victoria, el conductor recibe ofertas y casi nunca gana. */
const TASA_BAJA = 0.1;

/** Cuántos incidentes se listan en el bloque "recientes". El resto vive en `/incidentes`. */
const INCIDENTES_RECIENTES = 8;

/**
 * Etiquetas de los enums REALES de incidentes. Deliberadamente NO reusa `TIPO_INCIDENTE` /
 * `Etiqueta` de `componentes/Etiqueta.tsx`, que era lo que usaba la versión mock: ese diccionario
 * está indexado por los tipos MOCK de `dominio/contratos.ts`, que **no tienen** `DISPUTA_RUTA`,
 * `NO_SHOW` ni `CONDUCTA` y **sí tienen** un `RESUELTO` que el backend no tiene. Reusarlo habría
 * compilado y pintado `undefined` para tres de los seis tipos reales.
 *
 * ⚠️ **Duplica a propósito los diccionarios de `Incidentes.tsx`** en vez de importarlos de esa
 * pantalla: una página no debería depender de otra página (un cambio de layout allá rompería
 * acá). La duplicación es SEGURA porque el tipo es `Record<TipoIncidenteReal, …>`: si mañana se
 * agrega un valor al enum en `dominio/incidentesApi.ts` —la única fuente de verdad— **los dos
 * archivos dejan de compilar a la vez**. El compilador es el que mantiene la copia sincronizada,
 * no la memoria de quien edite.
 */
const TIPO_TEXTO: Record<TipoIncidenteReal, string> = {
  SOS: 'SOS',
  ACCIDENTE: 'Accidente',
  DISPUTA_RUTA: 'Disputa de ruta',
  NO_SHOW: 'No se presentó',
  CONDUCTA: 'Conducta',
  OTRO: 'Otro',
};

/** Sin `RESUELTO`: el backend real sólo tiene estos tres. */
const ESTADO_TEXTO: Record<EstadoIncidenteReal, [string, Tono]> = {
  ABIERTO: ['Abierto', 'malo'],
  EN_ATENCION: ['En atención', 'atencion'],
  CERRADO: ['Cerrado', 'neutro'],
};

export function Panel() {
  /*
    DOS llamadas, y la segunda no es un capricho: `GET /consola/panel` devuelve el CONTADOR de
    incidentes abiertos, no la LISTA. La tabla "Incidentes recientes" que esta pantalla mostraba
    con datos de ejemplo no tiene fuente en ese endpoint, así que se trae de
    `GET /consola/incidentes` — que es real, ya estaba cableado (`incidentesApi.ts`) y ya lo usa
    `Incidentes.tsx`. Las alternativas eran borrar la tabla (perder información que el operador
    usa) o dejarla en mock (mitad real, mitad inventada, sin que se note cuál es cuál); ninguna
    de las dos es mejor que una segunda llamada a un endpoint real.

    Cargas SEPARADAS a propósito: si el listado de incidentes falla, las cifras y el reparto se
    siguen viendo, y viceversa. Una sola carga combinada haría que cualquiera de las dos fallas
    dejara la pantalla entera en blanco.
  */
  const [panel, recargarPanel] = useCargaReal(() => obtenerPanel(), []);
  const [incidentes, recargarIncidentes] = useCargaReal(() => obtenerColaIncidentes(), []);
  const ahora = new Date();

  /*
    El backend los ordena por `abiertoAt` ASCENDENTE (el más viejo primero, que es lo que sirve
    para atender una cola). Acá el bloque se llama "recientes", así que se invierte y se recorta.
    No se filtran los cerrados: la cifra de arriba ya dice cuántos siguen abiertos, y esconder los
    cerrados haría que un incidente atendido hace diez minutos desapareciera sin dejar rastro.
  */
  const recientes = useMemo(() => {
    if (incidentes.estado !== 'listo') return [];
    return [...incidentes.datos]
      .sort((a, b) => new Date(b.abiertoAt).getTime() - new Date(a.abiertoAt).getTime())
      .slice(0, INCIDENTES_RECIENTES);
  }, [incidentes]);

  if (!hayBackend) {
    return (
      <Pagina
        rotulo="Turno"
        titulo="Panel"
        ayuda="Lo que está pasando ahora y lo que hay que atender. Todo lo demás cuelga de acá."
      >
        <div className="bloque">
          <Vacio
            titulo="Sin backend configurado"
            detalle="Falta VITE_API_URL. Este panel resume el estado real de la operación, así que sin backend no hay nada que resumir — y no se inventan datos de ejemplo."
          />
        </div>
      </Pagina>
    );
  }

  return (
    <Pagina
      rotulo="Turno"
      titulo="Panel"
      ayuda="Lo que está pasando ahora y lo que hay que atender. Todo lo demás cuelga de acá."
    >
      {panel.estado === 'cargando' ? (
        <div className="bloque">
          <Cargando filas={3} etiqueta="Cargando el estado de la operación" />
        </div>
      ) : panel.estado === 'error' ? (
        <div className="bloque">
          <ConError detalle={panel.mensaje} alReintentar={recargarPanel} />
        </div>
      ) : (
        <>
          <div className="cifras">
            <Cifra
              rotulo="Disponibles ahora"
              valor={panel.datos.conductoresDisponibles}
              nota={`${panel.datos.conductoresActivos} conectados · ${panel.datos.conductoresSuscritosActivos} con suscripción activa`}
            />
            <Cifra
              rotulo="Buscando conductor"
              valor={panel.datos.viajesBuscandoConductor}
              nota={`${panel.datos.viajesEnCurso} en curso`}
              tono={panel.datos.viajesBuscandoConductor > 0 ? 'atencion' : undefined}
            />
            <Cifra
              rotulo="Nadie los tomó"
              valor={panel.datos.viajesSinConductorHoy}
              /*
                «hoy» según el reloj del backend en UTC, no en hora de Lima (ver
                `dominio/panelApi.ts`): entre las 19:00 y la medianoche de Perú este contador ya
                cuenta el día siguiente. La pantalla no lo puede corregir sin contradecir al
                backend, así que lo dice.
              */
              nota="hoy (día UTC del servidor)"
              tono={panel.datos.viajesSinConductorHoy > 0 ? 'alarma' : undefined}
            />
            <Cifra
              rotulo="En cola de verificación"
              valor={panel.datos.conductoresEnColaVerificacion}
              tono={panel.datos.conductoresEnColaVerificacion > 0 ? 'atencion' : undefined}
            />
            <Cifra
              rotulo="Incidentes abiertos"
              valor={panel.datos.incidentesAbiertos}
              tono={panel.datos.incidentesAbiertos > 0 ? 'alarma' : undefined}
            />
          </div>

          {/*
            CA-10.4 — este bloque es la lectura que dice si el criterio de dispatch está
            cumpliendo la función objetivo del negocio.

            Con tarifa plana el ingreso es «conductores suscritos × tarifa»: no depende de la
            cantidad de viajes, sino de que los conductores renueven. Un reparto concentrado hace
            que la cola larga no renueve.

            🔑 Y por eso hacen falta las DOS columnas, no una: bajo oferta simultánea el reparto
            de OFERTAS puede verse perfectamente parejo mientras el de VIAJES se concentra. Sólo
            la tasa de victoria separa «nadie tiene viajes» de «este conductor pierde siempre».

            🔑 El `diagnostico` lo CALCULA el backend (`tasa-victoria.service.ts`) y se muestra
            tal cual: derivarlo acá de la mediana y el p10 sería una segunda fórmula para la
            misma pregunta, y las dos se desincronizarían en la primera corrección de umbral.
          */}
          <Bloque
            titulo="Reparto del período"
            nota={`Ofertas recibidas contra viajes ganados, por conductor, entre el ${panel.datos.tasaVictoria.resumen.desde.slice(0, 10)} y el ${panel.datos.tasaVictoria.resumen.hasta.slice(0, 10)}. Las dos columnas juntas: por separado no dicen nada.`}
            sinRelleno
            acciones={
              <Link className="boton boton--chico" to="/dispatch">
                Ver parámetros
              </Link>
            }
          >
            {panel.datos.tasaVictoria.reparto.length === 0 ? (
              <div className="bloque__cuerpo">
                <Vacio
                  titulo="Ninguna oferta en el período"
                  detalle={
                    panel.datos.tasaVictoria.resumen.diagnostico === 'SIN_MUESTRA'
                      ? 'Nadie compitió por un viaje en esta ventana, así que no se puede decir nada sobre el reparto.'
                      : 'No hay ofertas registradas en la ventana del informe.'
                  }
                />
              </div>
            ) : (
              <Tabla
                cabeceras={[
                  'Conductor',
                  { texto: 'Ofertas', num: true },
                  { texto: 'Ganados', num: true },
                  { texto: 'Tasa de victoria', num: true },
                  '',
                ]}
              >
                {panel.datos.tasaVictoria.reparto.map((fila) => {
                  const bajo = fila.tasaVictoria !== null && fila.tasaVictoria < TASA_BAJA;
                  return (
                    <tr
                      key={fila.conductorId}
                      data-estado={
                        bajo ? 'malo' : fila.tasaVictoria === null ? 'atencion' : undefined
                      }
                    >
                      <td>
                        {/* El backend ya cae al id cuando no hay nombre: nunca fila muda. */}
                        {fila.conductorNombre}
                        <div className="apagado">{fila.conductorId}</div>
                      </td>
                      <td className="tabla__num">{fila.ofertasRecibidas}</td>
                      <td className="tabla__num">{fila.viajesGanados}</td>
                      <td className="tabla__num">
                        {fila.tasaVictoria === null ? (
                          <span className="apagado">sin ofertas</span>
                        ) : (
                          porcentaje(fila.tasaVictoria)
                        )}
                      </td>
                      <td style={{ width: 130 }}>
                        <Barra fraccion={fila.tasaVictoria} bajo={bajo} />
                      </td>
                    </tr>
                  );
                })}
              </Tabla>
            )}
          </Bloque>
        </>
      )}

      <Bloque
        titulo="Incidentes recientes"
        /*
          Se dice de dónde sale este bloque porque NO sale del mismo endpoint que las cifras: son
          dos llamadas, y por eso el contador «Incidentes abiertos» de arriba y esta lista pueden
          discrepar por un instante si algo cambia entre una y otra. No es un error a esconder —
          es el precio de que cada bloque falle por separado.
        */
        nota="Los últimos que se abrieron, de la cola de incidentes. El contador de arriba viene del resumen, así que puede adelantarse por un momento."
        sinRelleno
        acciones={
          <Link className="boton boton--chico" to="/incidentes">
            Ver todos
          </Link>
        }
      >
        {incidentes.estado === 'cargando' ? (
          <div className="bloque__cuerpo">
            <Cargando filas={4} etiqueta="Cargando incidentes" />
          </div>
        ) : incidentes.estado === 'error' ? (
          <div className="bloque__cuerpo">
            <ConError detalle={incidentes.mensaje} alReintentar={recargarIncidentes} />
          </div>
        ) : recientes.length === 0 ? (
          <div className="bloque__cuerpo">
            <Vacio
              titulo="Ningún incidente registrado"
              detalle="Todavía no se abrió ninguno. Un SOS o una disputa aparecen acá apenas se reportan."
            />
          </div>
        ) : (
          <Tabla cabeceras={['Abierto', 'Tipo', 'Estado', 'Viaje', 'Atiende', 'Qué pasó']}>
            {recientes.map((incidente) => {
              const [textoEstado, tonoEstado] = ESTADO_TEXTO[incidente.estado];
              return (
                <tr
                  key={incidente.id}
                  data-estado={incidente.estado === 'ABIERTO' ? 'malo' : undefined}
                >
                  <td className="tenue">{haceCuanto(incidente.abiertoAt, ahora)}</td>
                  <td>{TIPO_TEXTO[incidente.tipo]}</td>
                  <td>
                    <span className={`etiqueta etiqueta--${tonoEstado}`}>{textoEstado}</span>
                  </td>
                  <td>
                    {incidente.viajeId ? (
                      <Link to="/viajes">{incidente.viajeId}</Link>
                    ) : (
                      <span className="apagado">—</span>
                    )}
                  </td>
                  {/*
                    🔴 El backend devuelve `operadorId`, NUNCA `operadorNombre`: `incidentes.
                    service.ts` hace `findMany` sin `include` de la relación. Se muestra el id
                    crudo, igual que `Incidentes.tsx` — es con lo que se sigue el hilo; un
                    nombre inventado o un guion no servirían para nada.
                  */}
                  <td className="tenue">
                    {incidente.operadorId ?? <span className="apagado">nadie todavía</span>}
                  </td>
                  <td style={{ fontFamily: 'var(--sans)' }}>
                    {incidente.descripcion ?? <span className="apagado">sin descripción</span>}
                  </td>
                </tr>
              );
            })}
          </Tabla>
        )}
      </Bloque>
    </Pagina>
  );
}
